import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import * as Sentry from "@sentry/react";

import { RequestError } from "../api/api-connector";
import { alert } from "../components/ui-kit/dialog-service";
import {
  networkErrorContent,
  requestErrorContent,
  accessDeniedContent,
} from "./error-dialog-content";
import { isProbablyNetworkError } from "./error-text";

export function initGlobalErrorHandling() {
  window.addEventListener("error", (event) => {
    handleError(event.error).catch(logErrorHandlingFailure);
  });

  window.addEventListener("unhandledrejection", (event) => {
    handleError(event.reason).catch(logErrorHandlingFailure);
  });

  window.handleError = handleError;
}

export async function handleError(error: unknown) {
  if (isProbablyNetworkError(error)) {
    await alert(networkErrorContent(), i18n._(msg`Connection error`));
  } else if (error instanceof RequestError) {
    if (error.handled) {
      // The connector already answered it: a logout is under way, or the user
      // dismissed the password prompt and the request went with it.
      return;
    }

    if (error.response?.status === 403) {
      await alert(accessDeniedContent(error), i18n._(msg`Access denied`));
    } else {
      await alert(requestErrorContent(error), i18n._(msg`Request error`));
    }
  } else {
    reportError(error);
  }
}

/**
 * Report an error only if {@link handleError} would have — that is, if it is
 * not an expected API or network failure. For screens that render the reason
 * themselves rather than letting it reach the global dialog: a suppressed
 * dialog shouldn't mean a genuine bug goes unnoticed.
 */
export function maybeReportError(error: unknown) {
  if (!isProbablyNetworkError(error) && !(error instanceof RequestError)) {
    reportError(error);
  }
}

/**
 * Report an unexpected error to the console and error tracking, without
 * surfacing it to the user. Use for "should never happen" cases that we still
 * want visibility into.
 */
export function reportError(error: unknown) {
  console.error(error);
  Sentry.captureException(error);
}

export function logErrorHandlingFailure(error: unknown) {
  console.error(error);
}
